/**
 * Contact page — Functional contact form using backend API.
 *
 * Sends form data to POST /api/contact on pairs.bigflowdata.com
 * which emails info@bigflowdata.com via Gmail API.
 */

import { useState, useEffect } from 'react'
import { Mail, MessageCircle, Send, AlertCircle } from 'lucide-react'

const API_URL = import.meta.env.VITE_API_URL || 'https://pairs.bigflowdata.com'

export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    document.title = 'Contact — bigflowdata'
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok) {
        setIsSubmitted(true)
      } else {
        setError(data.error || 'Something went wrong. Please try again.')
      }
    } catch {
      setError('Could not connect to the server. Please try again later.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="pt-16">
      {/* ============================================================
          HERO
          ============================================================ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-500/20 rounded-full blur-[128px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-24">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl lg:text-5xl font-black mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-[var(--color-text-secondary)]">
              Have a question, need help, or want to learn more? We'd love
              to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================
          CONTACT OPTIONS + FORM
          ============================================================ */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          {/* Email Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="card-gradient rounded-2xl p-8 border border-[var(--color-border)] text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-brand-500/10 flex items-center justify-center">
                <Mail className="h-8 w-8 text-brand-500" />
              </div>
              <h3 className="text-xl font-bold mb-3">Email Us</h3>
              <p className="text-[var(--color-text-muted)] mb-4">
                For general inquiries and partnerships
              </p>
              <a
                href="mailto:info@bigflowdata.com"
                className="text-brand-500 hover:text-brand-400 font-medium"
              >
                info@bigflowdata.com
              </a>
            </div>

            <div className="card-gradient rounded-2xl p-8 border border-[var(--color-border)] text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-brand-500/10 flex items-center justify-center">
                <MessageCircle className="h-8 w-8 text-brand-500" />
              </div>
              <h3 className="text-xl font-bold mb-3">Support</h3>
              <p className="text-[var(--color-text-muted)] mb-4">
                For technical support and billing questions
              </p>
              <a
                href="mailto:support@bigflowdata.com"
                className="text-brand-500 hover:text-brand-400 font-medium"
              >
                support@bigflowdata.com
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="card-gradient rounded-2xl p-8 border border-[var(--color-border)] max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-center">
              Send us a message
            </h2>

            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-brand-500/10 flex items-center justify-center">
                  <Send className="h-8 w-8 text-brand-500" />
                </div>
                <h3 className="text-xl font-bold mb-2">Message Sent!</h3>
                <p className="text-[var(--color-text-muted)]">
                  Thanks for reaching out. We'll get back to you as soon as possible.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      maxLength={200}
                      className="w-full px-4 py-3 rounded-xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:outline-none focus:border-brand-500 transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      maxLength={320}
                      className="w-full px-4 py-3 rounded-xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:outline-none focus:border-brand-500 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Subject</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:outline-none focus:border-brand-500 transition-colors"
                    placeholder="How can we help?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    maxLength={5000}
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:outline-none focus:border-brand-500 resize-none transition-colors"
                    placeholder="Tell us more..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>Sending...</>
                  ) : (
                    <>
                      Send Message
                      <Send className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
